// Low/mid/high levels of the default audio input, for the `fft` drive. The input is open only while a driven param needs it.
const BANDS_HZ = { low: [20, 250], mid: [250, 2000], high: [2000, 12000] }
// The dB span mapped onto 0…1.
const FLOOR_DB = -90
const CEIL_DB = -25

let ctx = null
let analyser = null
let input = null
let stream = null
let data = null
let wanted = false
let status = 'idle'
let readAt = -1
const levels = { low: 0, mid: 0, high: 0 }
const listeners = new Set()

const setStatus = s => {
	if (status === s) return
	status = s
	for (const fn of listeners) fn()
}

const syncStatus = () => setStatus(ctx?.state === 'running' ? 'on' : 'suspended')

/** `idle` · `starting` · `on` · `suspended` (waiting for a press) · `denied` · `error`. */
export const audioStatus = () => status

export const onAudioStatus = fn => {
	listeners.add(fn)
	return () => listeners.delete(fn)
}

// Bumped by every open and close: a request a later one superseded stops its tracks and touches nothing else.
let request = 0

const open = async () => {
	const id = ++request
	setStatus('starting')
	try {
		const s = await navigator.mediaDevices.getUserMedia({
			audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
		})
		if (id !== request) {
			for (const track of s.getTracks()) track.stop()
			return
		}
		stream = s
		ctx ??= new AudioContext()
		analyser = ctx.createAnalyser()
		analyser.fftSize = 2048
		analyser.smoothingTimeConstant = 0.6
		input = ctx.createMediaStreamSource(s)
		input.connect(analyser)
		data = new Float32Array(analyser.frequencyBinCount)
		ctx.onstatechange = syncStatus
		syncStatus()
	} catch (e) {
		if (id === request) setStatus(e?.name === 'NotAllowedError' ? 'denied' : 'error')
	}
}

const close = () => {
	request++
	for (const track of stream?.getTracks() ?? []) track.stop()
	input?.disconnect()
	if (ctx) ctx.onstatechange = null
	stream = input = analyser = data = null
	setStatus('idle')
}

const owners = new Set()

/** Keep the input open while any `owner` needs it. A refused input is not retried until `primeAudio`. */
export const needAudio = (on, owner = 'drives') => {
	if (on) owners.add(owner)
	else owners.delete(owner)
	if (owners.size > 0 === wanted) return
	wanted = !wanted
	if (wanted) open()
	else close()
}

/** From a user gesture: make (or wake) the context the autoplay policy would otherwise hold suspended, and retry a refused input. */
export const primeAudio = () => {
	ctx ??= new AudioContext()
	if (ctx.state === 'suspended') ctx.resume().then(() => analyser && syncStatus())
	if (wanted && (status === 'denied' || status === 'error')) open()
}

// A context made outside a gesture (a preset loading an `fft` drive) wakes on the next press.
if (typeof document !== 'undefined')
	document.addEventListener('pointerdown', () => ctx?.state === 'suspended' && primeAudio(), true)

/** A band's level, 0…1, or null while the input is closed; the spectrum is read once per tick time `t`. */
export const bandLevel = (band, t) => {
	if (!analyser) return null
	if (t !== readAt) {
		readAt = t
		analyser.getFloatFrequencyData(data)
		const hz = ctx.sampleRate / analyser.fftSize
		for (const [name, [lo, hi]] of Object.entries(BANDS_HZ)) {
			let sum = 0
			let n = 0
			for (let i = Math.max(1, Math.floor(lo / hz)); i <= Math.min(data.length - 1, Math.ceil(hi / hz)); i++) {
				sum += Math.max(FLOOR_DB, data[i])
				n++
			}
			const db = n ? sum / n : FLOOR_DB
			levels[name] = Math.min(1, Math.max(0, (db - FLOOR_DB) / (CEIL_DB - FLOOR_DB)))
		}
	}
	return levels[band] ?? 0
}
